import React, {useCallback, useMemo, useState} from 'react';

import {useCountries} from 'src/hooks/dictionaries';

import {Autosuggest} from '@pb/kit-component-autosuggest-v2';

import {CountriesList} from './countries-list';

import type {DictionaryItem} from 'src/openapi/admin/models/DictionaryItem';


type Props = {
    id?: string,
    selectedCountry?: string,
    onSelect: (countryCode: string) => void,
    disabled?: boolean,
    isAbsolute?: boolean,
    error?: boolean,
};

const preparedAutosuggestItem = (item: string) => {
    const last = item.toLowerCase().split('|').findLast(Boolean);

    return last
        ? last.trim().replace(/[()/-]/g, ' ').split(' ').map((word: string) => word.trim()).filter(Boolean).join(' ')
        : '';
};

const checkWord: (item: string, query: string) => boolean = (item: string, query: string) => {
    if (item.startsWith(query)) {
        return true;
    }

    const buffer = item.split(' ').slice(1).join(' ');

    if (buffer) {
        return checkWord(buffer, query);
    } else {
        return false;
    }
};


export const CountrySuggest = ({onSelect, disabled, id, error, selectedCountry}: Props) => {
    const [searchedQuery, setQuery] = useState<string>('');
    const [shown, setShown] = useState(false);
    const {countries} = useCountries();

    const suggestions = useMemo(
        () => (
            searchedQuery
                ? countries.filter(
                        ({description}) => checkWord(
                            preparedAutosuggestItem(description),
                            preparedAutosuggestItem(searchedQuery),
                        ),
                    ) : countries),
        [countries, searchedQuery],
    );

    const clearSuggest = useCallback(() => {
        setQuery('');
        setShown(false);
    }, []);

    const onItemSelect = useCallback((item: DictionaryItem) => {
        onSelect(item.code);
        clearSuggest();
    }, [clearSuggest, onSelect]);

    const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        onSelect('');
        setQuery(e.target.value);
        setShown(true);
    }, [onSelect]);

    const inputValue = selectedCountry
        ? countries.find(({code}) => code === selectedCountry)?.description || ''
        : searchedQuery;

    return (
        <div id={id} style={{minWidth: 230}}>
            <Autosuggest
                value={inputValue}
                shown={shown}
                setShown={setShown}
                onClick={() => setShown(true)}
                onChange={onChange}
                onBlur={clearSuggest}
                onItemSelect={onItemSelect}
                placeholder="Country"
                disabled={disabled}
                color={error ? 'ERROR' : undefined}>
                <CountriesList suggestions={suggestions}/>
            </Autosuggest>
        </div>
    );
};
